import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { businessApi, eventApi } from '../services/api';
import { Business, Event } from '../types';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface CheckInBooking {
  id: string; status: string; checkedIn: boolean; checkedInAt?: string; qrCode?: string;
  client: { firstName: string; lastName: string; email: string; phone?: string };
}

export function CheckIn() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [bookings, setBookings] = useState<CheckInBooking[]>([]);
  const [stats, setStats] = useState({ total: 0, checkedIn: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [scanMode, setScanMode] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const scanRef = useRef<HTMLInputElement>(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (selectedEvent) loadCheckins(); }, [selectedEvent]);
  useEffect(() => { if (scanMode) scanRef.current?.focus(); }, [scanMode]);

  const token = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  const fetchData = async () => {
    try {
      const bRes = await businessApi.getMyBusinesses();
      const list = bRes.data.data.businesses || [];
      setBusinesses(list);
      if (list.length > 0) {
        setSelectedBiz(list[0].id);
        const eRes = await eventApi.getBusinessEvents(list[0].id);
        setEvents(eRes.data.data.events || []);
      }
    } catch { toast.error('Error cargando datos'); }
    finally { setLoading(false); }
  };

  const handleBizChange = async (bizId: string) => {
    setSelectedBiz(bizId);
    setSelectedEvent('');
    setBookings([]);
    try {
      const eRes = await eventApi.getBusinessEvents(bizId);
      setEvents(eRes.data.data.events || []);
    } catch {}
  };

  const loadCheckins = async () => {
    try {
      const r = await axios.get(`${API_URL}/checkin/event/${selectedEvent}`, { headers: token() });
      setBookings(r.data.data.bookings || []);
      setStats(r.data.data.stats || { total: 0, checkedIn: 0, pending: 0 });
    } catch { toast.error('Error cargando inscritos'); }
  };

  const handleManualCheckin = async (bookingId: string) => {
    try {
      await axios.post(`${API_URL}/checkin/manual/${bookingId}`, {}, { headers: token() });
      toast.success('✅ Check-in realizado');
      loadCheckins();
    } catch { toast.error('Error en check-in'); }
  };

  const handleQRScan = async () => {
    if (!scanInput.trim()) return;
    try {
      let qrCode = scanInput.trim();
      try { const parsed = JSON.parse(qrCode); qrCode = parsed.qrCode || qrCode; } catch {}
      const r = await axios.post(`${API_URL}/checkin/scan`, { qrCode }, { headers: token() });
      if (r.data.data.alreadyCheckedIn) {
        toast.error('⚠️ Este cliente ya hizo check-in');
      } else {
        const client = r.data.data.booking?.client;
        toast.success(`✅ Check-in: ${client?.firstName} ${client?.lastName}`);
      }
      setScanInput('');
      loadCheckins();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'QR no válido');
      setScanInput('');
    }
  };

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return b.client?.firstName?.toLowerCase().includes(q) ||
      b.client?.lastName?.toLowerCase().includes(q) ||
      b.client?.email?.toLowerCase().includes(q);
  });

  const checkedInPct = stats.total > 0 ? Math.round((stats.checkedIn / stats.total) * 100) : 0;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-5">
      <style>{`
        .ci-stat { background: white; border-radius: 14px; border: 1px solid #e2e8f0; padding: 18px 20px; text-align: center; }
        .ci-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: white; border: 1px solid #e2e8f0; border-radius: 12px; margin-bottom: 8px; transition: all .2s; }
        .ci-row.checked { background: #f0fdf4; border-color: #86efac; }
        .ci-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; color: white; font-weight: 700; font-size: .88rem; flex-shrink: 0; }
        .ci-avatar.checked { background: linear-gradient(135deg, #10b981, #059669); }
        .scan-box { background: #0f172a; border-radius: 16px; padding: 24px; text-align: center; border: 2px dashed #334155; }
        .scan-input { background: #1e293b; border: 1px solid #334155; color: white; border-radius: 10px; padding: 12px 16px; font-size: .92rem; width: 100%; outline: none; font-family: inherit; margin-top: 12px; }
        .scan-input:focus { border-color: #3b82f6; }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">✅ Check-in</h1>
          <p className="text-secondary-500 text-sm mt-1">Registra la asistencia de tus clientes</p>
        </div>
        <button onClick={() => setScanMode(!scanMode)}
          style={{ background: scanMode ? '#fee2e2' : '#1e293b', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 10, fontWeight: 700, fontSize: '.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
          {scanMode ? '✕ Cerrar escáner' : '📷 Escanear QR'}
        </button>
      </div>

      {/* SELECCIÓN NEGOCIO/EVENTO */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {businesses.length > 1 && (
            <div>
              <label className="label">Negocio</label>
              <select className="input" value={selectedBiz} onChange={e => handleBizChange(e.target.value)}>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label">Evento</label>
            <select className="input" value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
              <option value="">— Seleccionar evento —</option>
              {events.map(e => (
                <option key={e.id} value={e.id}>
                  {e.title} · {new Date(e.startTime).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' })}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedEvent && (
        <>
          {/* STATS */}
          <div className="grid grid-cols-3 gap-4">
            <div className="ci-stat">
              <p className="text-2xl font-bold text-secondary-900">{stats.total}</p>
              <p className="text-xs text-secondary-400 mt-1">Total inscritos</p>
            </div>
            <div className="ci-stat" style={{ borderColor: '#86efac', background: '#f0fdf4' }}>
              <p className="text-2xl font-bold text-green-600">{stats.checkedIn}</p>
              <p className="text-xs text-secondary-400 mt-1">Han llegado</p>
            </div>
            <div className="ci-stat" style={{ borderColor: '#fcd34d', background: '#fef9c3' }}>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-xs text-secondary-400 mt-1">Pendientes</p>
            </div>
          </div>

          {/* BARRA ASISTENCIA */}
          <div className="card p-4">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: '.82rem', fontWeight: 600, color: '#475569' }}>Asistencia</span>
              <span style={{ fontSize: '.82rem', fontWeight: 800, color: checkedInPct >= 80 ? '#10b981' : '#f59e0b' }}>{checkedInPct}%</span>
            </div>
            <div style={{ background: '#f1f5f9', borderRadius: 100, height: 8 }}>
              <div style={{ height: 8, borderRadius: 100, width: `${checkedInPct}%`, background: checkedInPct >= 80 ? '#10b981' : '#3b82f6', transition: 'width .5s' }}></div>
            </div>
          </div>

          {/* SCANNER QR */}
          {scanMode && (
            <div className="scan-box">
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📷</div>
              <p style={{ color: '#94a3b8', fontSize: '.85rem', marginBottom: 4 }}>Escanea el QR del cliente o pégalo aquí</p>
              <p style={{ color: '#64748b', fontSize: '.72rem' }}>El campo se activa automáticamente al escanear</p>
              <input
                ref={scanRef}
                className="scan-input"
                value={scanInput}
                onChange={e => setScanInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleQRScan()}
                placeholder="Escanea el QR aquí..."
                autoFocus
              />
              <button onClick={handleQRScan} disabled={!scanInput.trim()}
                style={{ marginTop: 12, background: '#3b82f6', color: 'white', border: 'none', padding: '10px 24px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: scanInput.trim() ? 1 : .5 }}>
                ✅ Confirmar check-in
              </button>
            </div>
          )}

          {/* BÚSQUEDA */}
          <input
            type="text"
            className="input"
            placeholder="🔍 Buscar cliente por nombre o email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          {/* LISTA INSCRITOS */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <p style={{ fontSize: '.82rem', fontWeight: 600, color: '#475569' }}>
                {filtered.length} clientes {search ? 'encontrados' : 'inscritos'}
              </p>
              <div style={{ display: 'flex', gap: 8, fontSize: '.72rem' }}>
                <span style={{ color: '#10b981' }}>✅ = Check-in hecho</span>
                <span style={{ color: '#f59e0b' }}>⏳ = Pendiente</span>
              </div>
            </div>

            {filtered.map(b => (
              <div key={b.id} className={`ci-row ${(b as any).checkedIn ? 'checked' : ''}`}>
                <div className={`ci-avatar ${(b as any).checkedIn ? 'checked' : ''}`}>
                  {b.client?.firstName?.charAt(0)}{b.client?.lastName?.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 700, fontSize: '.88rem', color: '#1e293b' }}>{b.client?.firstName} {b.client?.lastName}</p>
                  <p style={{ fontSize: '.72rem', color: '#64748b' }}>{b.client?.email}</p>
                  {(b as any).checkedInAt && (
                    <p style={{ fontSize: '.68rem', color: '#10b981', marginTop: 2 }}>
                      ✅ Check-in: {new Date((b as any).checkedInAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {b.client?.phone && (
                    <a href={`https://wa.me/${b.client.phone.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
                      style={{ width: 32, height: 32, borderRadius: 8, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '.9rem' }}>
                      💬
                    </a>
                  )}
                  {!(b as any).checkedIn ? (
                    <button onClick={() => handleManualCheckin(b.id)}
                      style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, fontSize: '.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                      ✅ Check-in
                    </button>
                  ) : (
                    <span style={{ background: '#dcfce7', color: '#15803d', fontSize: '.72rem', fontWeight: 700, padding: '6px 12px', borderRadius: 8 }}>
                      ✓ Asistió
                    </span>
                  )}
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 24px', color: '#94a3b8' }}>
                <div style={{ fontSize: '2rem', marginBottom: 10 }}>👥</div>
                <p>{search ? 'No encontrado' : 'Sin inscritos en este evento'}</p>
              </div>
            )}
          </div>
        </>
      )}

      {!selectedEvent && (
        <div className="card p-12 text-center">
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📅</div>
          <p className="text-secondary-500 font-medium">Selecciona un evento para hacer check-in</p>
        </div>
      )}
    </div>
  );
}
